import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PlusIcon, StarIcon, MoonStarsIcon, CoinsIcon, BedIcon, UserIcon, XIcon, UsersThreeIcon, TrashIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { useIsMaster } from "@/lib/auth";
import { useAuth } from "@/lib/auth";
import { GUEST } from "@/constants/testIds";
import GuestKyc from "@/components/GuestKyc";
import { GuestContactEdit } from "@/components/GuestContactEdit";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const TIER_STYLE = {
  standard: "bg-muted text-muted-foreground",
  silver: "bg-[hsl(0_0%_88%)] text-[hsl(0_0%_25%)]",
  gold: "bg-[hsl(45_82%_88%)] text-[hsl(38_62%_35%)]",
  platinum: "bg-primary/15 text-primary",
};

function Guest360Dialog({ guestId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/guests/${guestId}/360`).then((r) => setData(r.data)).catch(() => {}); }, [guestId]);
  if (!data) return <DialogContent className="max-h-[90vh] overflow-y-auto"><p className="p-6">Loading Guest 360…</p></DialogContent>;
  const { guest, stats, stays, invoices, pos, halls, amenities, timeline } = data;
  const iconFor = { stay: BedIcon, invoice: CoinsIcon, pos: StarIcon, hall: UsersThreeIcon, amenity: MoonStarsIcon };
  const colorFor = { stay: "bg-primary/10 text-primary", invoice: "bg-secondary/10 text-secondary", pos: "bg-[hsl(340_60%_92%)] text-[hsl(340_60%_35%)]", hall: "bg-[hsl(200_60%_92%)] text-[hsl(200_70%_35%)]", amenity: "bg-muted text-muted-foreground" };
  return (
    <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="guest-360-dialog">
      <DialogHeader>
        <div className="flex items-center gap-3">
          <Avatar className="h-14 w-14"><AvatarFallback className="font-display text-lg">{guest.name?.[0]}</AvatarFallback></Avatar>
          <div>
            <p className="label-overline">Guest 360</p>
            <DialogTitle className="font-display text-2xl">{guest.name}</DialogTitle>
            <p className="text-xs text-muted-foreground">{guest.phone}{guest.email && ` · ${guest.email}`}</p>
          </div>
        </div>
      </DialogHeader>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mt-2">
        <Card className="p-3"><p className="label-overline">Stays</p><p className="font-display text-xl mt-1">{stats.total_stays}</p></Card>
        <Card className="p-3"><p className="label-overline">Nights</p><p className="font-display text-xl mt-1">{stats.nights_stayed}</p></Card>
        <Card className="p-3"><p className="label-overline">Room spend</p><p className="font-display text-xl mt-1">{inr(stats.total_spend)}</p></Card>
        <Card className="p-3"><p className="label-overline">F&amp;B</p><p className="font-display text-xl mt-1">{inr(stats.total_pos)}</p></Card>
        <Card className="p-3"><p className="label-overline">Halls</p><p className="font-display text-xl mt-1">{inr(stats.total_halls)}</p></Card>
        <Card className="p-3"><p className="label-overline">Amenities</p><p className="font-display text-xl mt-1">{inr(stats.total_amenities)}</p></Card>
        <Card className="p-3 bg-primary/5 border-primary/40"><p className="label-overline text-primary">Lifetime value</p><p className="font-display text-xl mt-1 text-primary">{inr(stats.lifetime_value)}</p></Card>
      </div>

      <Tabs defaultValue="timeline" className="mt-6">
        <TabsList data-testid="guest-360-tabs">
          <TabsTrigger value="timeline" data-testid="guest-360-tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="stays" data-testid="guest-360-tab-stays">Stays · {stays.length}</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="guest-360-tab-invoices">Invoices · {invoices.length}</TabsTrigger>
          <TabsTrigger value="fnb" data-testid="guest-360-tab-fnb">F&amp;B · {pos.length}</TabsTrigger>
          <TabsTrigger value="halls" data-testid="guest-360-tab-halls">Halls · {halls.length}</TabsTrigger>
          <TabsTrigger value="amenities" data-testid="guest-360-tab-amenities">Amenities · {amenities.length}</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-4">
          <div className="space-y-2" data-testid="guest-360-timeline-list">
            {timeline.length === 0 && <p className="text-center text-muted-foreground py-10 text-sm">No activity yet.</p>}
            {timeline.map((t, i) => {
              const Icon = iconFor[t.type] || CoinsIcon;
              return (
                <div key={i} className="flex items-start gap-3 border-b border-border/60 pb-2" data-testid={`guest-360-timeline-item-${i}`}>
                  <div className={`rounded-full p-2 ${colorFor[t.type] || ""} shrink-0`}><Icon size={14} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-medium text-sm truncate">{t.title}</p>
                      <p className="text-[10px] text-muted-foreground font-mono-plex whitespace-nowrap">{t.at}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{t.subtitle}{t.property_name && ` · ${t.property_name}`}</p>
                  </div>
                  {t.amount !== undefined && t.amount !== null && (
                    <p className="font-mono-plex text-sm font-semibold text-right shrink-0">{inr(t.amount)}</p>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="stays" className="mt-4">
          <Table>
            <TableHeader><TableRow><TableHead>Dates</TableHead><TableHead>Property</TableHead><TableHead>Room</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {stays.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-xs font-mono-plex">{s.check_in} → {s.check_out}</TableCell>
                  <TableCell className="text-xs">{s.property_name || "—"}</TableCell>
                  <TableCell className="text-xs">{s.room_number} · {s.room_type}</TableCell>
                  <TableCell className="text-xs"><Badge variant="outline">{s.plan_code || "EP"}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{s.status}</Badge></TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(s.total_amount)}</TableCell>
                </TableRow>
              ))}
              {stays.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6 text-xs">No stays yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Table>
            <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead></TableRow></TableHeader>
            <TableBody>
              {invoices.map((i) => (
                <TableRow key={i.id}>
                  <TableCell className="font-mono-plex text-xs">{i.number}</TableCell>
                  <TableCell className="text-xs">{(i.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><Badge variant="outline">{i.status}</Badge></TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(i.total)}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(i.paid || 0)}</TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-xs">No invoices yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="fnb" className="mt-4">
          <Table>
            <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Type</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {pos.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="font-mono-plex text-xs">{o.order_no}</TableCell>
                  <TableCell className="text-xs">{o.order_type}</TableCell>
                  <TableCell className="text-xs">{(o.items || []).map((it) => `${it.name} × ${it.qty}`).join(", ")}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(o.total)}</TableCell>
                </TableRow>
              ))}
              {pos.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6 text-xs">No F&amp;B orders yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="halls" className="mt-4">
          <Table>
            <TableHeader><TableRow><TableHead>Event</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {halls.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="text-xs">{h.purpose}</TableCell>
                  <TableCell className="text-xs">{(h.start_at || "").slice(0, 10)}</TableCell>
                  <TableCell><Badge variant="outline">{h.status}</Badge></TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(h.total_amount)}</TableCell>
                </TableRow>
              ))}
              {halls.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6 text-xs">No hall bookings yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="amenities" className="mt-4">
          <Table>
            <TableHeader><TableRow><TableHead>Slot</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {amenities.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="text-xs">{(a.slot_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell><Badge variant="outline">{a.status}</Badge></TableCell>
                  <TableCell className="text-right font-mono-plex">{inr((a.price || 0) + (a.gst_amount || 0))}</TableCell>
                </TableRow>
              ))}
              {amenities.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6 text-xs">No amenity bookings yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
}


function GuestDetailDialog({ guestId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const load = () => api.get(`/guests/${guestId}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [guestId]);

  const save = async (patch) => {
    await api.patch(`/guests/${guestId}`, patch);
    toast.success("Updated");
    load();
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    save({ tags: [...(data.guest.tags || []), tagInput.trim()] });
    setTagInput("");
  };
  const removeTag = (t) => save({ tags: (data.guest.tags || []).filter((x) => x !== t) });

  if (!data) return <DialogContent className="max-h-[90vh] overflow-y-auto"><p className="p-6">Loading…</p></DialogContent>;
  const g = data.guest;

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14"><AvatarFallback className="font-display text-lg">{g.name?.[0]}</AvatarFallback></Avatar>
          <div>
            <DialogTitle className="font-display text-2xl">{g.name}</DialogTitle>
            <p className="text-sm text-muted-foreground" data-testid="guest-360-contact">{g.phone}{g.email && ` · ${g.email}`}</p>
            {g.address && <p className="text-xs text-muted-foreground" data-testid="guest-360-address">{g.address}</p>}
            <GuestContactEdit guest={g} onSaved={() => { load(); onChanged?.(); }} testIdPrefix="guest-360-contact" />
          </div>
        </div>
      </DialogHeader>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mt-2">
        <Card className="p-3"><div className="flex items-center gap-2 text-xs text-muted-foreground"><BedIcon size={14} />Stays</div><p className="font-display text-2xl mt-1">{data.stats.total_stays}</p></Card>
        <Card className="p-3"><div className="flex items-center gap-2 text-xs text-muted-foreground"><MoonStarsIcon size={14} />Nights</div><p className="font-display text-2xl mt-1">{data.stats.nights_stayed}</p></Card>
        <Card className="p-3"><div className="flex items-center gap-2 text-xs text-muted-foreground"><CoinsIcon size={14} />Total spend</div><p className="font-display text-2xl mt-1">{inr(data.stats.total_spend)}</p></Card>
        <Card className="p-3"><div className="flex items-center gap-2 text-xs text-muted-foreground"><StarIcon size={14} />Tier</div>
          <Select value={g.loyalty_tier} onValueChange={(v) => save({ loyalty_tier: v })}>
            <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["standard", "silver", "gold", "platinum"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>
      </div>

      {/* Mandatory KYC (photo + ID) */}
      <div className="mt-6">
        <GuestKyc guest={g} onSaved={load} />
      </div>

      {/* Guest preferences & stay history */}
      <div className="mt-6">
        <GuestPreferences guest={g} onSaved={load} />
      </div>

      {/* Billing tax IDs — for direct-billed guests to appear on invoices */}
      <div className="mt-6">
        <p className="label-overline mb-2">Tax IDs (optional)</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">GSTIN</Label>
            <Input
              defaultValue={g.gstin || ""}
              placeholder="e.g. 29ABCDE1234F1Z5"
              maxLength={15}
              onBlur={(e) => e.target.value.toUpperCase() !== (g.gstin || "") && save({ gstin: e.target.value.toUpperCase() || null })}
              className="mt-1 uppercase"
              data-testid="guest-360-gstin"
            />
          </div>
          <div>
            <Label className="text-xs">PAN</Label>
            <Input
              defaultValue={g.pan || ""}
              placeholder="e.g. ABCDE1234F"
              maxLength={10}
              onBlur={(e) => e.target.value.toUpperCase() !== (g.pan || "") && save({ pan: e.target.value.toUpperCase() || null })}
              className="mt-1 uppercase"
              data-testid="guest-360-pan"
            />
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Printed on invoices when the Admin Console → Invoice layout has &ldquo;Guest GSTIN / PAN&rdquo; enabled.</p>
      </div>

      {/* Tags */}
      <div className="mt-6">
        <p className="label-overline">Tags</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {(g.tags || []).map((t) => (
            <Badge key={t} variant="outline" className="pl-3 pr-1">
              {t}
              <button onClick={() => removeTag(t)} className="ml-1 hover:text-destructive" data-testid={`guest-tag-remove-${t}`}><XIcon size={12} /></button>
            </Badge>
          ))}
          <div className="flex items-center gap-1">
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag (VIP, celeb, etc.)" className="h-8 w-40 text-xs" data-testid="guest-tag-input" />
            <Button size="sm" variant="outline" onClick={addTag} data-testid="guest-tag-add-btn">Add</Button>
          </div>
        </div>
      </div>

      {/* Private notes */}
      <div className="mt-6">
        <p className="label-overline">Private notes</p>
        <Textarea
          defaultValue={g.notes || ""}
          rows={3}
          placeholder="Preferences, allergies, birthdays…"
          onBlur={(e) => e.target.value !== (g.notes || "") && save({ notes: e.target.value })}
          data-testid="guest-notes-textarea"
        />
      </div>

      {/* Stay history */}
      <div className="mt-6">
        <p className="label-overline mb-2">Stay history</p>
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.stays.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-sm">{s.property?.name}</TableCell>
                  <TableCell className="text-sm">{s.room?.number} · {s.room?.room_type}</TableCell>
                  <TableCell className="text-xs">{s.check_in} → {s.check_out}</TableCell>
                  <TableCell><Badge variant="outline">{s.status.replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-right font-mono-plex text-sm">{inr(s.total_amount)}</TableCell>
                </TableRow>
              ))}
              {data.stays.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">No stays yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </DialogContent>
  );
}

function GuestPreferences({ guest, onSaved }) {
  const [form, setForm] = useState({
    room_type: guest.preferences?.room_type || "",
    food: guest.preferences?.food || "",
    pillow: guest.preferences?.pillow || "",
    dietary: guest.preferences?.dietary || "",
    notes: guest.preferences?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm({
      room_type: guest.preferences?.room_type || "",
      food: guest.preferences?.food || "",
      pillow: guest.preferences?.pillow || "",
      dietary: guest.preferences?.dietary || "",
      notes: guest.preferences?.notes || "",
    });
  }, [guest.id]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/guests/${guest.id}`, { preferences: form });
      toast.success("Preferences saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const history = guest.stay_history || [];
  return (
    <div className="rounded-lg border border-border p-4 bg-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-medium">Guest preferences</p>
          <p className="text-xs text-muted-foreground">Wow them on arrival — we auto-remember room type after their first stay.</p>
        </div>
        <p className="text-xs text-muted-foreground">{guest.total_stays || 0} past stay{(guest.total_stays || 0) === 1 ? "" : "s"}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Preferred room type</Label>
          <Input value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} placeholder="Deluxe, Suite…" className="h-9 mt-1" data-testid="pref-room-type" />
        </div>
        <div>
          <Label className="text-xs">Preferred pillow</Label>
          <Input value={form.pillow} onChange={(e) => setForm({ ...form, pillow: e.target.value })} placeholder="Firm / soft / feather" className="h-9 mt-1" />
        </div>
        <div>
          <Label className="text-xs">Food preferences</Label>
          <Input value={form.food} onChange={(e) => setForm({ ...form, food: e.target.value })} placeholder="Continental breakfast, no eggs…" className="h-9 mt-1" />
        </div>
        <div>
          <Label className="text-xs">Dietary needs</Label>
          <Input value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} placeholder="Jain / vegan / gluten-free…" className="h-9 mt-1" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Notes for front desk</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Loves a mountain-facing room…" className="h-9 mt-1" data-testid="pref-notes" />
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <Button size="sm" onClick={save} disabled={saving} data-testid="pref-save-btn">{saving ? "Saving…" : "Save preferences"}</Button>
      </div>
      {history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Past stays</p>
          <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
            {history.slice().reverse().slice(0, 6).map((h, i) => (
              <li key={i} className="flex justify-between text-muted-foreground">
                <span>{h.property_name} · {h.room_type} {h.room_number}</span>
                <span className="font-mono-plex">{h.check_in} → {h.check_out}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Guests() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [openGuest, setOpenGuest] = useState(null);
  const [open360, setOpen360] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", nationality: "Indian", id_type: "aadhar", id_number: "", address: "", gstin: "", pan: "" });
  const [q, setQ] = useState("");
  const [dupOpen, setDupOpen] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [merging, setMerging] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isMaster = useIsMaster();

  const load = () => api.get("/guests").then((r) => setItems(r.data));
  const loadDups = () => api.get("/guests/duplicates").then((r) => setDupGroups(r.data));
  useEffect(() => { load(); loadDups(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/guests", form);
      toast.success("Guest saved");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", nationality: "Indian", id_type: "aadhar", id_number: "", address: "", gstin: "", pan: "" });
      load(); loadDups();
    } catch { toast.error("Failed to save guest"); }
  };

  const doDelete = async (force) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/guests/${deleteTarget.id}`, { params: force ? { force: "true" } : {} });
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load(); loadDups();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Delete failed";
      if (/active reservation/i.test(detail)) {
        // offer force delete
        if (window.confirm(`${detail}\n\nForce-delete this guest AND all their reservations & invoices?`)) {
          try {
            await api.delete(`/guests/${deleteTarget.id}`, { params: { force: "true" } });
            toast.success(`${deleteTarget.name} force-deleted`);
            setDeleteTarget(null);
            load(); loadDups();
          } catch (err) { toast.error(err?.response?.data?.detail || "Force delete failed"); }
        }
      } else {
        toast.error(detail);
      }
    }
    finally { setDeleting(false); }
  };

  const mergeGroup = async (group) => {
    const [primary, ...dupes] = group.guests;
    if (!window.confirm(`Merge ${dupes.length + 1} guest(s) into "${primary.name}"?\nAll reservations & invoices will be reassigned.`)) return;
    setMerging(true);
    try {
      await api.post("/guests/merge", { primary_id: primary.id, duplicate_ids: dupes.map((d) => d.id) });
      toast.success("Merged");
      load(); loadDups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setMerging(false); }
  };

  const filtered = q ? items.filter((g) => (g.name + " " + g.phone + " " + (g.email || "")).toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div>
      <PageHeader
        overline="Directory"
        title="Guests"
        description="Single directory across all properties · click a guest for their 360° profile."
        actions={
          <div className="flex gap-2">
            <Input placeholder="Search name, phone, email…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="guest-search" />
            {dupGroups.length > 0 && (
              <Button variant="outline" onClick={() => setDupOpen(true)} data-testid="guest-dupes-btn">
                <UsersThreeIcon size={14} className="mr-1" />{dupGroups.length} duplicate{dupGroups.length > 1 && "s"}
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid={GUEST.newBtn}><PlusIcon size={16} className="mr-1" />New guest</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Add guest</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="guest-name" /></div>
                    <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="guest-phone" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Email <span className="text-muted-foreground text-[10px] font-normal">(optional)</span></Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Leave blank if not available" data-testid="guest-email" /></div>
                    <div><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>ID type</Label><Input value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} /></div>
                    <div><Label>ID number</Label><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} /></div>
                  </div>
                  <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>GSTIN <span className="text-muted-foreground text-[10px] font-normal">(optional)</span></Label><Input value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} maxLength={15} placeholder="e.g. 29ABCDE1234F1Z5" data-testid="guest-gstin" /></div>
                    <div><Label>PAN <span className="text-muted-foreground text-[10px] font-normal">(optional)</span></Label><Input value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} maxLength={10} placeholder="e.g. ABCDE1234F" data-testid="guest-pan" /></div>
                  </div>
                  <DialogFooter><Button type="submit" data-testid={GUEST.submitBtn}>Save</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>KYC</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((g) => (
              <TableRow key={g.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setOpenGuest(g.id)} data-testid={`guest-row-${g.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7"><AvatarFallback className="text-xs">{g.name?.[0]}</AvatarFallback></Avatar>
                    {g.name}
                  </div>
                </TableCell>
                <TableCell>{g.phone}</TableCell>
                <TableCell className="text-muted-foreground">{g.email || "—"}</TableCell>
                <TableCell>
                  {g.photo && g.id_card_image ? (
                    <Badge variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">Ready</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]">Missing</Badge>
                  )}
                </TableCell>
                <TableCell><Badge variant="outline" className={TIER_STYLE[g.loyalty_tier || "standard"]}>{g.loyalty_tier || "standard"}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {(g.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                    {(g.tags?.length || 0) > 3 && <span className="text-xs text-muted-foreground">+{g.tags.length - 3}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => setOpen360(g.id)} data-testid={`guest-360-btn-${g.id}`} title="Guest 360 timeline">
                      <ClockCounterClockwiseIcon size={14} />
                    </Button>
                    {isMaster && (
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(g)} data-testid={`guest-delete-btn-${g.id}`} title="Delete guest (master only)" className="text-destructive hover:bg-destructive/10">
                        <TrashIcon size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No guests found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {openGuest && (
        <Dialog open onOpenChange={(v) => !v && setOpenGuest(null)}>
          <GuestDetailDialog guestId={openGuest} onClose={() => setOpenGuest(null)} onChanged={load} />
        </Dialog>
      )}

      {open360 && (
        <Dialog open onOpenChange={(v) => !v && setOpen360(null)}>
          <Guest360Dialog guestId={open360} onClose={() => setOpen360(null)} />
        </Dialog>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="guest-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <TrashIcon size={18} /> Delete guest
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Delete <strong>{deleteTarget?.name}</strong>? This removes the guest profile only. Active reservations, if any, will block deletion — you&apos;ll be prompted to force-delete which also wipes their reservations &amp; invoices.</p>
            <div className="rounded-md bg-muted/60 p-3 text-xs space-y-1">
              <div><span className="text-muted-foreground">Phone:</span> {deleteTarget?.phone || "—"}</div>
              <div><span className="text-muted-foreground">Email:</span> {deleteTarget?.email || "—"}</div>
              <div><span className="text-muted-foreground">Total stays:</span> {deleteTarget?.stay_count || 0}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={() => doDelete(false)} disabled={deleting} data-testid="guest-delete-confirm">
              <TrashIcon size={14} className="mr-1" /> {deleting ? "Deleting…" : "Delete guest"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Duplicate guests</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Merging keeps the first record; all reservations, invoices and tags from the others are moved onto it.</p>
          <div className="space-y-3 mt-4">
            {dupGroups.map((g, i) => (
              <Card key={i} className="p-4">
                <p className="label-overline mb-2">Matched by {g.match_by}: <span className="font-mono-plex normal-case">{g.key}</span></p>
                <ul className="text-sm space-y-1">
                  {g.guests.map((gu, j) => (
                    <li key={gu.id} className="flex items-center gap-2">
                      {j === 0 ? <Badge variant="outline" className="bg-primary/10 text-primary">Keep</Badge> : <Badge variant="outline">Merge</Badge>}
                      <span className="font-medium">{gu.name}</span>
                      <span className="text-muted-foreground text-xs">· {gu.phone}{gu.email && ` · ${gu.email}`}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-end mt-3">
                  <Button size="sm" onClick={() => mergeGroup(g)} disabled={merging} data-testid={`guest-merge-${i}`}>Merge {g.guests.length} → 1</Button>
                </div>
              </Card>
            ))}
            {dupGroups.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No duplicates found. Nicely done.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
